import React, { useEffect, useRef, useState } from 'react';
import './SpaceBackground.css';

const SpaceBackground = () => {
  const canvasRef = useRef(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let stars = [];
    let shootingStars = [];

    // Responsive canvas sizing
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };

    // Initialize stars based on screen size
    const initStars = () => {
      const isMobile = window.innerWidth < 768;
      const starCount = isMobile ? 80 : 150;
      stars = [];

      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 1.5 + 0.5,
          opacity: Math.random() * 0.8 + 0.2,
          twinkleSpeed: Math.random() * 0.02 + 0.005,
          twinklePhase: Math.random() * Math.PI * 2,
          driftX: (Math.random() - 0.5) * 0.1,
          driftY: (Math.random() - 0.5) * 0.05,
        });
      }
    };

    // Create shooting star
    const createShootingStar = () => {
      if (prefersReducedMotion) return;
      
      const startX = Math.random() * canvas.width * 0.8;
      const startY = Math.random() * canvas.height * 0.4;
      
      shootingStars.push({
        x: startX,
        y: startY,
        length: Math.random() * 80 + 40,
        speed: Math.random() * 8 + 6,
        angle: Math.PI / 4 + (Math.random() - 0.5) * 0.3,
        opacity: 1,
        trail: [],
      });
    };

    // Animation loop
    const animate = (timestamp) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw and update stars
      stars.forEach((star) => {
        // Twinkle effect
        if (!prefersReducedMotion) {
          star.twinklePhase += star.twinkleSpeed;
          star.opacity = 0.3 + Math.sin(star.twinklePhase) * 0.4 + 0.3;
          
          // Subtle drift
          star.x += star.driftX;
          star.y += star.driftY;

          // Wrap around
          if (star.x < 0) star.x = canvas.width;
          if (star.x > canvas.width) star.x = 0;
          if (star.y < 0) star.y = canvas.height;
          if (star.y > canvas.height) star.y = 0;
        }

        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();

        // Add glow for larger stars
        if (star.radius > 1) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.radius * 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * 0.15})`;
          ctx.fill();
        }
      });

      // Draw and update shooting stars
      if (!prefersReducedMotion) {
        shootingStars = shootingStars.filter((ss) => {
          ss.x += Math.cos(ss.angle) * ss.speed;
          ss.y += Math.sin(ss.angle) * ss.speed;
          ss.opacity -= 0.015;

          if (ss.opacity <= 0) return false;

          // Draw shooting star trail
          const gradient = ctx.createLinearGradient(
            ss.x - Math.cos(ss.angle) * ss.length,
            ss.y - Math.sin(ss.angle) * ss.length,
            ss.x,
            ss.y
          );
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
          gradient.addColorStop(0.5, `rgba(185, 173, 162, ${ss.opacity * 0.5})`);
          gradient.addColorStop(1, `rgba(255, 255, 255, ${ss.opacity})`);

          ctx.beginPath();
          ctx.moveTo(
            ss.x - Math.cos(ss.angle) * ss.length,
            ss.y - Math.sin(ss.angle) * ss.length
          );
          ctx.lineTo(ss.x, ss.y);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.stroke();

          // Head glow
          ctx.beginPath();
          ctx.arc(ss.x, ss.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${ss.opacity})`;
          ctx.fill();

          return true;
        });
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    // Initialize
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Start animation
    animate();

    // Shooting star interval (random between 3-8 seconds)
    let shootingStarInterval;
    if (!prefersReducedMotion) {
      const scheduleShootingStar = () => {
        const delay = Math.random() * 5000 + 3000;
        shootingStarInterval = setTimeout(() => {
          createShootingStar();
          scheduleShootingStar();
        }, delay);
      };
      scheduleShootingStar();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
      if (shootingStarInterval) clearTimeout(shootingStarInterval);
    };
  }, [prefersReducedMotion]);

  return (
    <div className="space-background" data-testid="space-background">
      <canvas ref={canvasRef} className="starfield-canvas" />
      <div className="moon-container">
        <div className={`moon-wrapper ${prefersReducedMotion ? 'no-animation' : ''}`}>
          <img 
            src="/moon-realistic.png" 
            alt="Moon" 
            className="moon-image"
          />
          <div className="moon-crater-overlay"></div>
          <div className="moon-shadow-overlay"></div>
          <div className="moon-glow-effect"></div>
        </div>
      </div>
    </div>
  );
};

export default SpaceBackground;
